import { ProductsDocument } from "@/generated/graphql";
import { useQuery } from "@apollo/client/react";

const PRODUCT_LIMIT = 500;

/**
 * Fetches and paginates products with search functionality.
 *
 * Queries the GraphQL API for products with support for load-more pagination
 * and debounced text search by product name.
 *
 * @param searchText - Search query to filter products by name (case-insensitive)
 * @returns An object containing:
 *   - products: Array of Product objects for the current page(s)
 *   - loading: Boolean indicating if the initial query is in progress
 *   - loadMore: Function to fetch the next page of products
 *   - hasMore: Boolean indicating if more products are available to load
 */
export function useProducts(searchText: string) {
  const { data, loading, fetchMore } = useQuery(ProductsDocument, {
    variables: {
      limit: PRODUCT_LIMIT,
      offset: 0,
      searchText: searchText || null,
    },
  });

  const items = data?.products?.items ?? [];
  const hasMore = data?.products?.hasMore ?? false;

  const loadMore = () => {
    fetchMore({
      variables: {
        limit: PRODUCT_LIMIT,
        offset: items.length,
        searchText: searchText || null,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return {
          products: {
            ...fetchMoreResult.products,
            items: [...prev.products.items, ...fetchMoreResult.products.items],
          },
        };
      },
    });
  };

  return { products: items, loading, loadMore, hasMore };
}
